import { Clipboard } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  MAT_BOTTOM_SHEET_DATA,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { environment as env } from '@environments/environment';
import { AccountRequirement } from '@models/events/account-requirement.enum';
import { EventAttendeeTicketOrderParticipantStatus } from '@models/events/enums/event-attendee-ticket-status.enum';
import { EventAttendeeTicket } from '@models/events/event-attendee-ticket.model';
import { Event } from '@models/events/event.model';
import { User } from '@models/users/user.model';
import { ClaimEventTicketDialog } from '@modules/events/dialogs/claim-event-ticket/claim-event-ticket.dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '@services/auth/auth.service';
import { EventService } from '@services/events/event.service';
import { NotificationService } from '@services/shared/notification.service';
import { FormsModules } from '@utils/shared-modules';

@Component({
  selector: 'app-forward-ticket-bottom-sheet',
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModules],
  providers: [EventService, NotificationService, TranslateService],
  templateUrl: './forward-ticket-bottom-sheet.component.html',
  styleUrl: './forward-ticket-bottom-sheet.component.scss',
})
export class ForwardTicketBottomSheetComponent implements OnInit {
  ticket?: EventAttendeeTicket;
  ticketDetail?: EventAttendeeTicket;
  event?: Event;
  form: FormGroup;
  loggedUser?: User;

  constructor(
    private bottomSheetRef: MatBottomSheetRef<ForwardTicketBottomSheetComponent>,
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: any,
    private eventService: EventService,
    private clipboard: Clipboard,
    private fb: FormBuilder,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private dialog: MatDialog,
    private authService: AuthService,
  ) {
    this.ticket = data.ticket;
    this.event = data.event;

    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });

    this.authService.userSubject
      .pipe(takeUntilDestroyed())
      .subscribe((user) => (this.loggedUser = user));
  }

  ngOnInit() {
    this.getTicketDetails();
  }

  get showAssignToMe() {
    if (!this.ticketDetail) return;

    return !this.ticketDetail.eventAttendeeTicketOrder?.hasBuyerTicket() && this.ticketDetail.status === EventAttendeeTicketOrderParticipantStatus.UNASSIGNED;
  }

  sendUnassignedTicket() {
    if (!this.event || !this.ticket) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.value;

    this.eventService
      .sendUnassignedTicket(this.event.id, this.ticket.uuid, value.email)
      .subscribe({
        next: () => {
          this.notificationService.success(
            this.translateService.instant(
              'APP.TICKETS_DASHBOARD.TICKET_INVITATION_SENT',
            ),
          );
        },
        error: () => {
          this.notificationService.success(
            this.translateService.instant(
              'APP.TICKETS_DASHBOARD.ERRORS.TICKET_INVITATION_SENT',
            ),
          );
        },
      });
  }

  getTicketDetails() {
    if (!this.event || !this.ticket) return;

    this.eventService
      .getEventTicketDetail(this.event.id, this.ticket.uuid)
      .subscribe((ticket) => {
        this.ticketDetail = ticket;
      });
  }

  openClaimTicketDialog() {
    const dialogRef = this.dialog.open(ClaimEventTicketDialog, {
      maxWidth: '100dvw',
      width: '100%',
      height: '100dvh',
      data: {
        ticket: this.ticketDetail,
        event: this.event,
        mainImage: this.event?.getEncodedMainImage(true),
        loggedUser: this.loggedUser,
        isAssignToMe: true,
      },
      disableClose: true,
      panelClass: [
        'normal-dialog',
        'new-normal-dialog',
        'book-event-slot-dialog',
        'no-overflow',
      ],
      backdropClass: '',
    });

    dialogRef.afterClosed().subscribe(() => {
      this.closeDrawer();
    });
  }
  copyTicketForwardLink() {
    if (!this.event || !this.ticket) {
      return;
    }

    this.clipboard.copy(
      `${env.website}/events/${this.event.uri}?ticketUuid=${this.ticket.uuid}`,
    );

    this.notificationService.success(
      this.translateService.instant('APP.TICKETS_DASHBOARD.TICKET_LINK_COPIED'),
    );
  }

  closeDrawer() {
    this.bottomSheetRef.dismiss();
  }

  protected readonly AccountRequirement = AccountRequirement;
}
